import isEqual from 'lodash/isEqual';

export type FormErrorType =
    | 'validate'
    | 'required'
    | 'pattern'
    | 'minLength'
    | 'maxLength'
    | 'min'
    | 'max'
    | 'value'
    | 'setValueAs'
    | 'shouldUnregister'
    | 'onChange'
    | 'onBlur'
    | 'disabled'
    | 'deps'
    | 'pattern'
    | 'valueAsNumber'
    | 'valueAsDate';

/**
 * Checks if the error type is a standard form validation error
 * @param errorType - The type of error to check
 * @returns boolean indicating if it's a standard form validation error
 */
export const isFormValidationError = (errorType?: string): boolean => {
    const validationErrorTypes: FormErrorType[] = [
        'validate',
        'required',
        'pattern',
        'minLength',
        'maxLength',
        'min',
        'max',
        'value',
        'setValueAs',
        'shouldUnregister',
        'onChange',
        'onBlur',
        'disabled',
        'deps',
    ];

    return validationErrorTypes.includes(errorType as FormErrorType);
};

export type FilterFormValue =
    | string
    | string[]
    | { value: string; label: string }
    | { value: string; label: string }[];

export function getFilteredFormDataEntries(
    formData: Record<string, unknown>
): [string, FilterFormValue][] {
    return Object.entries(formData).filter(([_, item]) => {
        if (item === undefined || item === '') return false;

        if (Array.isArray(item)) {
            return item.length > 0;
        }

        if (typeof item === 'object' && item !== null) {
            const { value, label } = item as Record<string, string>;
            return !!value || !!label;
        }

        return true;
    }) as [string, FilterFormValue][];
}

export function getAllFormDataEntries(
    formData: Record<string, unknown>
): [string, FilterFormValue][] {
    return Object.entries(formData).filter(([_, item]) => {
        if (item === undefined || item === '') return false;

        if (Array.isArray(item)) {
            return true;
        }

        if (typeof item === 'object' && item !== null) {
            const { value, label } = item as Record<string, string>;
            return !!value || !!label;
        }

        return true;
    }) as [string, FilterFormValue][];
}

export function applyFilteredFormData(
    formData: Record<string, unknown>,
    onChange: (key: string, value: FilterFormValue) => void,
    previousFilters?: Record<string, FilterFormValue>
) {
    getAllFormDataEntries(formData).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length === 0) {
            if (previousFilters && key in previousFilters) {
                onChange(key, value);
            }
        } else {
            onChange(key, value);
        }
    });
}

export function normalizeFilters(
    filters?: Record<string, FilterFormValue>
): Record<string, string | string[]> {
    const result: Record<string, string | string[]> = {};

    if (!filters) return result;

    Object.entries(filters).forEach(([key, filterValue]) => {
        let normalizedValue: string | string[] | undefined;

        if (Array.isArray(filterValue)) {
            normalizedValue = filterValue
                .map((v) => (typeof v === 'string' ? v : v.value))
                .filter((v) => v !== 'ALL');

            if (normalizedValue.length === 0) {
                return;
            }
        } else if (typeof filterValue === 'object') {
            normalizedValue = filterValue.value;
        } else if (
            (typeof filterValue === 'boolean' && filterValue === false) ||
            filterValue === null
        )
            return;
        else {
            normalizedValue = filterValue;
        }
        if (normalizedValue !== 'ALL') {
            result[key] = normalizedValue;
        }
    });

    return result;
}

const sanitizeFilterDefaults = (
    obj: Record<string, unknown>
): Record<string, unknown> =>
    Object.fromEntries(
        Object.entries(obj).filter(
            ([, value]) =>
                value !== undefined &&
                value !== null &&
                value !== '' &&
                value !== 'ALL' &&
                !(Array.isArray(value) && value.length === 0)
        )
    );

export const areFiltersDirty = (
    current: Record<string, unknown>,
    initial?: Record<string, unknown>
): boolean => {
    return !isEqual(
        sanitizeFilterDefaults(current),
        sanitizeFilterDefaults(initial ?? {})
    );
};
